<template>
  <div class="service-provider-access-page">
    <div v-if="loading" class="loading-screen">
      <div class="loader"></div>
      <div class="loading-title">拍照任务加载中</div>
      <div class="loading-desc">正在匹配品牌、系列和拍摄点位</div>
    </div>

    <template v-else>
      <div class="header">
        <div class="img1">
          <img class="img" :src="headerImage">
          <div class="tag">{{ context.kindName || '商品' }}</div>
        </div>
        <div class="guide-title">
          <div class="name">
            <div class="title">{{ productTitle }}</div>
            <div v-if="orderNoText" class="meta-line">
              <span class="meta-label">订单</span>
              <span class="meta-value">{{ orderNoText }}</span>
            </div>
            <div class="service-line">{{ serviceText }}</div>
          </div>
        </div>
      </div>

      <div v-if="pageError" class="error-box">
        <div class="error-title">页面初始化失败</div>
        <div class="error-desc">{{ pageError }}</div>
        <button class="retry-btn" @click="loadContext">重试</button>
      </div>

      <div v-else class="body">
        <div class="body-header">
          <div class="main-title">
            <div class="title-left">
              <span class="label main-header">请按顺序拍摄清晰完整的鉴定点图片</span>
            </div>
          </div>
          <div class="tip-header">
            <template v-if="isSupplementMode">
              请重新拍摄标记点位，完成<span class="tip-strong">{{ displayMustNum }} 个重新拍摄点位</span>后可提交
            </template>
            <template v-else>
              <span class="necessity-tag">*</span>为必拍项，完成全部必拍项且<span class="tip-strong">至少拍摄 {{ displayMustNum }} 个点位</span>后可提交
            </template>
          </div>
        </div>

        <div class="progress-row">
          <div class="progress-track">
            <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <div class="progress-text">已拍点位 {{ successCount }}/{{ progressTotal }}</div>
        </div>

        <div v-if="!list.length" class="empty-box">
          <div class="empty-title">暂无可拍摄点位</div>
          <div class="empty-desc">请刷新重试，或确认当前商品型号是否已配置鉴定点位</div>
        </div>

        <div v-else class="list">
          <div
            v-for="(item, index) in list"
            :key="item.pointId || item.name || index"
            class="item"
            @click="selectImage(index)"
          >
            <div :class="['img-box', item.file ? 'has-file' : 'no-file', item.status === 'failed' ? 'is-warning' : '']">
              <img v-if="item.file" class="img" :src="item.file">
              <div v-if="item.status === 'uploading' || item.status === 'singleing'" class="mask">处理中</div>
              <div v-if="item.status === 'success'" class="success-tag">✓</div>
              <div v-if="item.status === 'failed'" class="failed-tag">!</div>
              <div v-if="item.needSupplement" class="supplement-tag">重新拍摄</div>
              <div v-if="!item.file" class="add-icon">+</div>
              <button
                v-if="item.file && item.status !== 'uploading' && item.status !== 'singleing'"
                class="cancel-image-btn"
                type="button"
                @click.stop="cancelImage(index)"
              >
                ×
              </button>
            </div>
            <div class="title">
              <span v-if="item.must === 1" class="necessity-tag">*</span>{{ item.name }}
            </div>
            <div class="point-status">{{ pointStatusText(item) }}</div>
            <div v-if="item.needSupplement && item.supplementReason" class="supplement-reason">
              {{ item.supplementReason }}
            </div>
            <div v-if="item.errorMsg" class="point-error">{{ item.errorMsg }}</div>
          </div>
        </div>
      </div>

      <div v-if="!pageError" class="footer">
        <div class="btn-acti">
          <button
            class="submit-btn"
            :disabled="submitDisabled"
            @click="handleSubmitOverall"
          >
            {{ submitText }}
          </button>
        </div>
      </div>

      <input
        ref="fileInput"
        class="hidden-input"
        type="file"
        accept="image/*"
        @change="onFileChange"
      >
    </template>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { debounce } from '@/common/utils'
import {
  queryAHSContext,
  submitAHSOverall,
  submitAHSSingle,
  uploadAHSImage
} from '@/common/api/ahs-adapter'

const DEFAULT_HEADER_IMAGE = 'https://oss.turingsenseai.com/1596794250941895693.png'
const CAMERA_POINTS_KEY = 'service_provider_camera_points'
const CAMERA_RESULT_KEY = 'service_provider_camera_result'

export default {
  name: 'ServiceProviderAccessPage',
  data () {
    return {
      headerImage: DEFAULT_HEADER_IMAGE,
      fallbackImage: DEFAULT_HEADER_IMAGE,
      loading: true,
      submitting: false,
      pageError: '',
      selectedIndex: -1,
      debouncedSubmitOverall: null,
      context: {},
      list: []
    }
  },
  computed: {
    internalTaskNo () {
      return this.$route.query.internalTaskNo || this.$route.query.taskNo || ''
    },
    partnerTaskNo () {
      return this.$route.query.partnerTaskNo || ''
    },
    token () {
      return this.$route.query.token || ''
    },
    taskParams () {
      return {
        internalTaskNo: this.internalTaskNo,
        taskNo: this.partnerTaskNo,
        token: this.token
      }
    },
    productTitle () {
      const brandName = this.context.brandName || ''
      const productName = this.context.seriesName || this.context.productName || this.context.productTitle || ''
      if (brandName && productName) {
        return `${brandName} · ${productName}`
      }
      if (brandName) {
        return brandName
      }
      if (productName) {
        return productName
      }
      return this.context.productId ? `商品型号 ${this.context.productId}` : '服务商鉴定'
    },
    orderNoText () {
      return this.context.orderNo || this.context.orderId || this.$route.query.orderNo || ''
    },
    serviceText () {
      return this.context.appraisalFlowType === 1 ? '图灵人工鉴定' : '图灵 AI 鉴定'
    },
    title () {
      return this.context.brandName || (this.context.productId ? `商品型号 ${this.context.productId}` : '服务商鉴定')
    },
    subtitle () {
      const flowName = this.context.appraisalFlowType === 1 ? '人工鉴定' : '智能鉴定'
      const productName = this.context.seriesName || this.context.kindName || ''
      return productName ? `${productName} · ${flowName}` : flowName
    },
    statusText () {
      const statusMap = {
        created: '待拍摄',
        shooting: '拍摄中',
        overalling: '鉴定中',
        waiting_manual: '待人工鉴定',
        need_supplement: '待重新拍摄',
        supplementing: '重新拍摄提交中',
        completed: '已完成'
      }
      return statusMap[this.context.status] || '处理中'
    },
    statusClass () {
      return this.isSubmitted ? 'is-completed' : ''
    },
    supplementPointIds () {
      const points = this.context.supplementPoints || []
      return points.map(item => String(item.pointId || item.pointID || '')).filter(Boolean)
    },
    supplementPointMap () {
      return (this.context.supplementPoints || []).reduce((map, item) => {
        const pointId = String(item.pointId || item.pointID || '')
        if (pointId) {
          map[pointId] = item
        }
        return map
      }, {})
    },
    isSupplementMode () {
      if (!this.supplementPointIds.length) {
        return false
      }
      return !['completed', 'waiting_manual'].includes(this.context.status)
    },
    isSubmitted () {
      const hasOAID = Boolean(this.context.oaid || this.context.OAID)
      return Boolean(this.context.status === 'completed' || this.context.status === 'waiting_manual' || (hasOAID && !this.isSupplementMode))
    },
    mustNum () {
      const pointList = this.context.pointList || {}
      return Number(pointList.mustNum || 0)
    },
    mustPointCount () {
      return this.list.filter(item => item.must === 1 || item.must === true || item.must === '1').length
    },
    requiredPointNum () {
      if (this.isSupplementMode) {
        return this.supplementRequiredPoints.length
      }
      return Math.max(this.mustPointCount, this.mustNum)
    },
    displayMustNum () {
      return this.requiredPointNum
    },
    progressTotal () {
      if (this.isSupplementMode) {
        return this.displayMustNum
      }
      return this.list.length || this.displayMustNum
    },
    progressPercent () {
      if (!this.progressTotal) {
        return 0
      }
      return Math.min(100, Math.round((this.successCount / this.progressTotal) * 100))
    },
    successCount () {
      if (this.isSupplementMode) {
        return this.supplementRequiredPoints.filter(item => item.status === 'success' && item.said).length
      }
      return this.list.filter(item => item.status === 'success' && item.said).length
    },
    supplementRequiredPoints () {
      if (!this.supplementPointIds.length) {
        return []
      }
      const pointIdSet = new Set(this.supplementPointIds)
      return this.list.filter(item => pointIdSet.has(String(item.pointId)))
    },
    changedNonSupplementPoints () {
      const pointIdSet = new Set(this.supplementPointIds)
      return this.list.filter(item => {
        if (!item.said || pointIdSet.has(String(item.pointId))) {
          return false
        }
        return item.said !== (item.originalSaid || '') || item.imageUrl !== (item.originalImageUrl || '')
      })
    },
    failedCount () {
      return this.list.filter(item => item.status === 'failed').length
    },
    progressText () {
      if (!this.list.length) {
        return '等待点位配置'
      }
      if (this.isSubmitted) {
        return '鉴定已提交完成'
      }
      if (this.failedCount) {
        return `${this.failedCount} 个点位需要重新上传`
      }
      if (this.submitDisabled) {
        return `至少完成 ${this.displayMustNum} 个有效点位后可提交`
      }
      return '已满足提交条件'
    },
    submitDisabled () {
      return this.submitting || this.loading || this.isSubmitted || !this.list.length || !this.isPointReady()
    },
    submitText () {
      if (this.submitting) {
        return '提交中...'
      }
      if (this.isSubmitted) {
        return '鉴定已完成'
      }
      if (this.isSupplementMode) {
        return '提交重新拍摄'
      }
      return '提交鉴定'
    }
  },
  created () {
    this.debouncedSubmitOverall = debounce(this.submitOverall, 300)
  },
  mounted () {
    this.loadContext()
  },
  activated () {
    this.consumeCameraResult()
  },
  methods: {
    async loadContext () {
      if (!this.internalTaskNo || !this.token) {
        this.pageError = 'URL 缺少 internalTaskNo/taskNo 或 token'
        this.loading = false
        return
      }
      this.loading = true
      this.pageError = ''
      try {
        const res = await queryAHSContext(this.taskParams)
        if (res.code !== 200) {
          throw new Error(res.resultMessage || '查询任务上下文失败')
        }
        const data = res.data || {}
        this.context = data
        this.headerImage = data.imageUrl || this.headerImage
        this.list = this.mergeUploadedPoints(this.normalizePoints(data.pointList), data.uploadedPoints || [])
        this.consumeCameraResult()
      } catch (err) {
        this.pageError = err.message || '查询任务上下文失败'
      } finally {
        this.loading = false
      }
    },
    normalizePoints (pointList) {
      const source = (pointList && pointList.totalPointInfos) || []
      return source.map((item, index) => ({
        ...item,
        index,
        pointId: String(item.pointId || item.pointID || ''),
        name: item.name || item.pointName || `点位${index + 1}`,
        must: Number(item.must || (item.necessity ? 1 : 2)),
        needSupplement: Boolean(item.needSupplement),
        supplementReason: item.supplementReason || '',
        supplementBatch: item.supplementBatch || 0,
        file: '',
        imageUrl: '',
        said: '',
        originalSaid: '',
        originalImageUrl: '',
        status: 'pending',
        errorMsg: ''
      }))
    },
    mergeUploadedPoints (points, uploadedPoints) {
      const uploadedMap = uploadedPoints.reduce((map, item) => {
        map[String(item.pointId)] = item
        return map
      }, {})
      if (!points.length) {
        return uploadedPoints.map((item, index) => ({
          index,
          pointId: String(item.pointId || ''),
          name: item.pointName || `点位${index + 1}`,
          must: 2,
          needSupplement: Boolean(this.supplementPointMap[String(item.pointId || '')]),
          supplementReason: (this.supplementPointMap[String(item.pointId || '')] || {}).reason || '',
          supplementBatch: (this.supplementPointMap[String(item.pointId || '')] || {}).batch || 0,
          file: item.imageUrl,
          imageUrl: item.imageUrl,
          said: item.said,
          originalSaid: item.said || '',
          originalImageUrl: item.imageUrl || '',
          status: item.said ? 'success' : 'pending',
          errorMsg: ''
        }))
      }
      return points.map((point) => {
        const uploaded = uploadedMap[String(point.pointId)]
        const supplement = this.supplementPointMap[String(point.pointId)]
        const mergedPoint = supplement ? {
          ...point,
          needSupplement: true,
          supplementReason: supplement.reason || point.supplementReason || '',
          supplementBatch: supplement.batch || point.supplementBatch || 0
        } : point
        if (!uploaded) {
          return mergedPoint
        }
        return {
          ...mergedPoint,
          file: uploaded.imageUrl,
          imageUrl: uploaded.imageUrl,
          said: uploaded.said,
          originalSaid: uploaded.said || '',
          originalImageUrl: uploaded.imageUrl || '',
          status: uploaded.said ? 'success' : 'pending'
        }
      })
    },
    selectImage (index) {
      const item = this.list[index]
      if (this.isSubmitted) {
        Toast('鉴定已完成')
        return
      }
      if (!item || item.status === 'uploading' || item.status === 'singleing') {
        return
      }
      this.selectedIndex = index
      this.saveCameraPoints()
      this.$router.push({
        path: '/identify/take-photo',
        query: {
          pointIndex: index,
          internalTaskNo: this.internalTaskNo,
          partnerTaskNo: this.partnerTaskNo,
          taskNo: this.partnerTaskNo,
          token: this.token,
          ...(this.$route.query.debug === '1' ? { debug: '1' } : {})
        }
      })
    },
    saveCameraPoints () {
      const points = this.list.map((item, index) => ({
        index,
        pointId: item.pointId,
        name: item.name,
        must: item.must === 1 || item.must === true,
        stickFigureImg: item.stickFigureImg || item.exampleImg || this.fallbackImage,
        exampleImg: item.exampleImg || item.stickFigureImg || this.fallbackImage,
        photoGuide: item.photoGuide || `请拍摄${item.name}，保证画面清晰完整`,
        file: item.file || '',
        imageUrl: item.imageUrl || '',
        said: item.said || '',
        originalSaid: item.originalSaid || '',
        originalImageUrl: item.originalImageUrl || '',
        status: item.status || 'pending',
        needSupplement: Boolean(item.needSupplement),
        supplementReason: item.supplementReason || '',
        supplementBatch: item.supplementBatch || 0,
        errorMsg: item.errorMsg || ''
      }))
      try {
        sessionStorage.setItem(CAMERA_POINTS_KEY, JSON.stringify({
          internalTaskNo: this.internalTaskNo,
          taskNo: this.partnerTaskNo,
          token: this.token,
          points
        }))
      } catch (err) {}
    },
    consumeCameraResult () {
      if (!this.list.length) {
        return
      }
      let result
      try {
        result = JSON.parse(sessionStorage.getItem(CAMERA_RESULT_KEY) || '')
      } catch (err) {
        result = null
      }
      if (!result || result.internalTaskNo !== this.internalTaskNo || !Array.isArray(result.files)) {
        return
      }
      sessionStorage.removeItem(CAMERA_RESULT_KEY)
      result.files.forEach((item) => {
        if (typeof item.index !== 'number' || !item.file || !this.list[item.index]) {
          return
        }
        if (item.imageUrl && item.said) {
          this.updatePoint(item.index, {
            file: item.file,
            imageUrl: item.imageUrl,
            said: item.said,
            status: 'success',
            errorMsg: '',
            detectionBox: item.detectionBox,
            originalBox: item.originalBox
          })
          return
        }
        this.handleImageSelected(item.index, item.file)
      })
    },
    cancelImage (index) {
      if (this.isSubmitted) {
        Toast('鉴定已完成')
        return
      }
      this.updatePoint(index, {
        file: '',
        imageUrl: '',
        said: '',
        status: 'pending',
        errorMsg: '',
        detectionBox: null,
        originalBox: null
      })
    },
    pointStatusText (item) {
      if (item.status === 'uploading') {
        return '上传中'
      }
      if (item.status === 'singleing') {
        return '识别中'
      }
      if (item.status === 'success') {
        return '点击可重拍'
      }
      if (item.status === 'failed') {
        return '点击重新上传'
      }
      if (item.needSupplement) {
        return '需重新拍摄'
      }
      return item.must === 1 ? '建议优先拍摄' : '可补充拍摄'
    },
    onFileChange (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file || this.selectedIndex < 0) {
        return
      }
      const reader = new FileReader()
      reader.onload = () => this.handleImageSelected(this.selectedIndex, reader.result)
      reader.readAsDataURL(file)
    },
    async handleImageSelected (index, dataURL) {
      this.updatePoint(index, {
        file: dataURL,
        status: 'uploading',
        errorMsg: '',
        said: '',
        imageUrl: ''
      })
      try {
        const uploadRes = await uploadAHSImage({
          ...this.taskParams,
          imgB64: this.stripBase64Prefix(dataURL)
        })
        if (uploadRes.code !== 200 || !uploadRes.data || !uploadRes.data.imageUrl) {
          throw new Error((uploadRes && uploadRes.resultMessage) || '图片上传失败')
        }
        const imageUrl = uploadRes.data.imageUrl
        this.updatePoint(index, { imageUrl, status: 'singleing' })
        await this.submitSingle(index, imageUrl)
      } catch (err) {
        const message = err.message || '图片处理失败'
        this.updatePoint(index, {
          status: 'failed',
          errorMsg: message
        })
        Toast(message)
      }
    },
    async submitSingle (index, imageUrl) {
      const point = this.list[index]
      const res = await submitAHSSingle({
        ...this.taskParams,
        pointId: point.pointId,
        pointName: point.name,
        imageUrl,
        payload: {
          source: 'ahs'
        }
      })
      const data = res.data || {}
      const errorCode = Number(data.error_code || data.errorCode || 0)
      if (res.code !== 200 || errorCode !== 0) {
        throw new Error(this.singleErrorMessage(data, res.resultMessage, '单点鉴定失败'))
      }
      if (data.detection === false) {
        throw new Error(this.singleErrorMessage(data, '', '图片未检测到有效鉴定点'))
      }
      if (!data.said) {
        throw new Error('单点鉴定未返回 said')
      }
      this.updatePoint(index, {
        said: data.said,
        status: 'success',
        errorMsg: '',
        detectionBox: data.detection_box || data.detectionBox,
        originalBox: data.original_box || data.originalBox
      })
      Toast.success('单点鉴定通过')
    },
    handleSubmitOverall () {
      if (this.submitting) {
        return
      }
      this.debouncedSubmitOverall()
    },
    async submitOverall () {
      if (this.isSubmitted) {
        Toast('鉴定已完成')
        return
      }
      if (this.submitDisabled) {
        Toast('请先补充足够的有效鉴定点')
        return
      }
      this.submitting = true
      try {
        const submitPoints = this.isSupplementMode
          ? this.supplementRequiredPoints.concat(this.changedNonSupplementPoints)
          : this.list
        const saidList = submitPoints.filter(item => item.said).map(item => item.said)
        const exterior = submitPoints.find(item => item.imageUrl) || this.list.find(item => item.imageUrl)
        const res = await submitAHSOverall({
          ...this.taskParams,
          saidList,
          exteriorUrl: exterior ? exterior.imageUrl : '',
          payload: {
            source: 'ahs'
          }
        })
        if (res.code !== 200) {
          throw new Error(res.resultMessage || '提交鉴定失败')
        }
        const data = res.data || {}
        const errorCode = Number(data.error_code || data.errorCode || 0)
        if (errorCode !== 0) {
          throw new Error(data.error_msg || data.errorMsg || '提交鉴定失败')
        }
        this.context = {
          ...this.context,
          status: data.status || (this.context.appraisalFlowType === 1 ? 'waiting_manual' : 'completed'),
          oaid: data.oaid || data.OAID || this.context.oaid
        }
        Toast.success('鉴定提交成功')
      } catch (err) {
        Toast(err.message || '提交鉴定失败')
      } finally {
        this.submitting = false
      }
    },
    isPointReady () {
      if (!this.list.length) {
        return false
      }
      if (!this.requiredPointNum) {
        return false
      }
      if (this.isSupplementMode) {
        return this.supplementRequiredPoints.length > 0 &&
          this.supplementRequiredPoints.every(item => item.status === 'success' && item.said)
      }
      const mustPointsReady = this.list
        .filter(item => item.must === 1 || item.must === true || item.must === '1')
        .every(item => item.status === 'success' && item.said)
      return mustPointsReady && this.successCount >= this.requiredPointNum
    },
    updatePoint (index, patch) {
      const list = [...this.list]
      list[index] = { ...list[index], ...patch }
      this.list = list
    },
    stripBase64Prefix (dataURL) {
      return String(dataURL || '').replace(/^data:image\/\w+;base64,/, '')
    },
    singleErrorMessage (data, fallback, defaultMessage) {
      const message = (data && (data.error_msg || data.errorMsg)) || fallback || ''
      return !message || /^success$/i.test(String(message)) ? defaultMessage : message
    }
  }
}
</script>

<style lang="stylus" src="./service-provider-access.styl"></style>
